import orderBy from "lodash/orderBy";
import { register } from "@rill/loader";
import appendTypeAndCountry from "../utils/append-type-and-country";
import {
  getCoachStripePlan,
  getCoachTrialStatus,
  calculateFreeTrialDaysLeft,
} from "../utils/user-helpers";

register(
  {
    name: "athlete",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { user }) => {
    if (!user) return;
    try {
      const res = await api("user-info", {
        query: {
          _id: user,
          customFields: [
            "name",
            "email",
            "avatar",
            "registered",
            "exerciseLibrary",
            "repMaximums",
            "preferredLoadInput",
            "percent",
            "units",
            "country",
            "admin",
            "permissions",
            "ambassador",
            "stripe",
            "primaryExercises",
            "tasks",
            "goals",
            "equipment",
            "limitations",
            "round",
            "coach._id",
            "coach.organizationId",
            "coach.name",
            "accountability",
          ],
        },
      });
      const data = await res.json();
      const { actualUser = {} } = data;
      if (actualUser.accountability) {
        actualUser.customAccountability = actualUser.accountability;
        delete actualUser.accountability;
      }
      return actualUser;
    } catch (_) {}
  }
);

register(
  {
    name: "coachOrganizationInformation",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { user }) => {
    if (!user) return;
    try {
      const res = await api("user-info", {
        query: {
          _id: user,
          customFields: [
            "athletes",
            "avatar",
            "name",
            "startingCalendarDay",
            "accountabilityTemplates",
            "questionnaireTemplates",
            "annualPhasesTemplates",
            "athletesGroups",
          ],
        },
      });
      const data = await res.json();
      const { actualUser = {} } = data;
      return actualUser;
    } catch (_) {}
  }
);

register(
  {
    name: "allAthletesFromOrganization",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api, locals }) => {
    const organizationId = locals.activeUser.organizationId;
    if (!organizationId) return;
    try {
      const res = await api(`athletes-request-organizations`);
      const data = await res.json();
      const { organizationAthletes = [] } = data;
      return organizationAthletes;
    } catch (_) {}
  }
);

register(
  {
    name: "activeUser",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { user }) => {
    if (!user) return;
    try {
      return await (await api(`user/${user}`)).json();
    } catch (_) {}
  }
);

register(
  {
    name: "invoice",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { user }) => {
    if (!user) return;
    try {
      return await (
        await api(`user-invoice`, {
          query: { userId: user },
        })
      ).json();
    } catch (_) {}
  }
);

register(
  {
    name: "userByEmail",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { email }) => {
    if (!email) return;
    try {
      const response = await (
        await api(`user-by-email`, { query: { email } })
      ).json();
      const { user } = response;
      return user;
    } catch (_) {}
  }
);

register(
  {
    name: "profileUser",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api, momentTZ }, { user }) => {
    if (!user) return;
    try {
      const res = await api("user-info", {
        query: {
          _id: user,
          customFields: [
            "country",
            "admin",
            "permissions",
            "ambassador",
            "stripe",
            "exerciseLibrary",
            "repMaximums",
            "primaryExercises",
            "email",
          ],
        },
      });
      const data = await res.json();
      const { actualUser: profileUser = {} } = data;
      return appendTypeAndCountry(profileUser, momentTZ);
    } catch (_) {}
  }
);

register(
  {
    name: "coaches",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async (
    { api },
    {
      name,
      email,
      trialStatus,
      sortBy = "name",
      sortType = "asc",
      onlyPush = false,
    }
  ) => {
    const query = {
      name,
      email,
      limit: false,
      sort: { name: 1 },
      onlyPush,
    };
    const res = await api("coaches", {
      query,
    });
    const resJSON = await res.json();

    let coaches = resJSON.coaches
      .map((coach) => {
        coach.trialStatus = getCoachTrialStatus(coach);
        coach.stripePlan = getCoachStripePlan(coach);
        const daysLeft = calculateFreeTrialDaysLeft(coach);
        coach.daysLeft = daysLeft > 0 ? daysLeft : 0;
        coach.daysSince = daysLeft < 0 ? Math.abs(daysLeft) : 0;
        return coach;
      })
      .filter((coach) => {
        if (trialStatus && trialStatus !== "any") {
          return coach.trialStatus === trialStatus;
        }
        return true;
      });

    coaches = orderBy(coaches, [sortBy], [sortType]);

    let totalAthletesActive = 0,
      totalAthletesInactive = 0,
      totalAthletes = 0,
      totalSubscribers = 0;

    coaches.map((coach) => {
      if (onlyPush) {
        totalSubscribers += parseInt(
          coach.subscribers ? coach.subscribers.length : 0
        );
        return;
      }
      totalAthletes += parseInt(coach.totalAthletes);
      totalAthletesActive += parseInt(coach.active);
      totalAthletesInactive += parseInt(coach.inactive);
    });

    return onlyPush
      ? { coaches, totalSubscribers }
      : {
          coaches,
          totalAthletes,
          totalAthletesActive,
          totalAthletesInactive,
        };
  }
);

register(
  {
    name: "admin_coaches",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { name, email }) => {
    const query = {
      permissions: "coach",
      limit: false,
      sort: { name: 1 },
    };
    if (name) {
      query["name"] = { $regex: "^.*" + name + ".*$", $options: "i" };
    }
    if (email) {
      query["email"] = { $regex: "^.*" + email + ".*$", $options: "i" };
    }
    const res = await api("user", {
      query,
    });
    const resJSON = await res.json();
    return await resJSON.filter(
      (user) => user.permissions.indexOf("coachCanSubmit") === -1
    );
  }
);

register(
  {
    name: "feBranding",
    meta: ["feBranding"],
  },
  async ({ api }, params) => {
    const res = await api("fe-branding", {});
    const resJSON = await res.json();

    return resJSON;
  }
);
register(
  {
    name: "feIncomes",
    meta: ["no-bust"],
    ttl: "2 hours",
  },
  async ({ api }, params) => {
    const selector = params.selector || 3;
    let query = {
      selector,
    };
    if (params && params.organizationUser) {
      query = {
        ...query,
        organizationUser: params.organizationUser,
      };
    }
    const res = await api(`fe-incomes`, {
      query,
    });
    const resJSON = await res.json();
    return resJSON;
  }
);

register(
  {
    name: "coachingBilling",
    meta: ["coachingBilling"],
  },
  async ({ api }, month, year) => {
    const res = await api("coaching-billing", {
      query: {
        month,
        year,
      },
    });
    const resJSON = await res.json();
    return resJSON;
  }
);

register(
  {
    name: "subscribersList",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { startingAfter = null }) => {
    const res = await api("stripe-connected-account-subs", {
      query: {
        startingAfter,
      },
    });
    const resJSON = await res.json();
    return resJSON;
  }
);

register(
  {
    name: "stripeRecentEvents",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { customerId = null }) => {
    const res = await api("stripe-recent-activity", {
      query: {
        customerId: customerId,
      },
    });
    const { customerEvents } = await res.json();
    return customerEvents;
  }
);

register(
  {
    name: "subscribersListExport",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { memberships = false, organizationUser = undefined }) => {
    let query = { memberships };
    if (organizationUser) {
      query = { memberships, organizationUser };
    }
    const res = await api("subscribers-list-export", {
      query,
    });
    const resJSON = await res.json();
    return resJSON;
  }
);

register(
  {
    name: "activeSubscribersList",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { organizationUser = undefined }) => {
    let query = {};
    if (organizationUser) {
      query = { organizationUser };
    }
    const res = await api("active-subscribers-list", {
      query,
    });
    const resJSON = await res.json();
    const data = resJSON?.allCommunityUsers || [];
    return data;
  }
);

register(
  {
    name: "subscriberInvoices",
    meta: ["no-bust"],
    ttl: "2 hours",
  },
  async ({ api }, { customer, product, organizationUser = undefined }) => {
    let query = { customer, product };
    if (organizationUser) {
      query = { customer, product, organizationUser };
    }
    const res = await api("subscriber-invoices", {
      query,
    });
    const resJSON = await res.json();
    return resJSON;
  }
);

register(
  {
    name: "upcomingInvoice",
    meta: ["no-bust"],
    ttl: "2 hours",
  },
  async ({ api }, { customer, price }) => {
    const res = await api("retrieve-upcoming-invoice", {
      query: { proxyUser: customer, price },
    });
    const resJSON = await res.json();
    return resJSON;
  }
);

register(
  {
    name: "pendingAthleteInvitations",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { user }) => {
    if (!user) return;
    try {
      const createInvitedUser = (email, arr) =>
        arr.filter((element) => element.email === email)[0] || {
          email: email,
          notRegistered: true,
        };

      const res = await api("user-info", {
        query: {
          _id: user,
          customFields: ["pendingAthleteInvitations"],
        },
      });
      const data = await res.json();
      const { actualUser = {} } = data;

      const invitedEmails = actualUser.pendingAthleteInvitations || [];

      // avoid making queries with empty arrays because in some edge cases it pulls the entire collection causing slow load times
      if (!invitedEmails || !invitedEmails.length) return [];

      const invitedRegisteredUsers =
        (await (
          await api("user", {
            query: {
              limit: false,
              email: { $in: invitedEmails },
              fields: ["name", "email", "avatar"],
            },
          })
        ).json()) || [];

      const invitedUsers = invitedEmails.map((invitedEmail) => {
        const user = createInvitedUser(invitedEmail, invitedRegisteredUsers);
        user.invited = true;
        return user;
      });

      return invitedUsers;
    } catch (_) {}
  }
);

register(
  {
    name: "coachingDashboardForMonth",
    meta: [
      "no-bust",
      "user",
      "exercise",
      "daily-stats",
      "coach-notes",
      "video-record",
    ],
    ttl: "2 hours",
  },
  async ({ api }, month, year, organizationCoach, metric) => {
    let query = {
      month,
      year,
    };
    if (organizationCoach) {
      query = { ...query, organizationCoach };
    }
    if (metric) {
      query = { ...query, metric };
    }
    const res = await api("coaching-dashboard-for-month", {
      query,
    });
    const resJSON = await res.json();
    return resJSON;
  }
);
register(
  {
    name: "coach",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { user }) => {
    if (!user) return;
    try {
      return await (
        await api(`user/${user}`, {
          query: {
            fields: [
              "permissions",
              "coachingFreeTrial",
              "accountabilityTemplates",
            ],
          },
        })
      ).json();
    } catch (_) {}
  }
);

register(
  {
    name: "coachingReceipts",
    meta: ["coachingReceipts"],
  },
  async ({ api }, startDateUnix, endDateUnix) => {
    const res = await api("stripe-invoice-bill", {
      query: {
        startDateUnix,
        endDateUnix,
      },
    });
    const resJSON = await res.json();

    return resJSON;
  }
);

register(
  {
    name: "pendingInvoices",
    meta: ["pendingInvoices"],
  },
  async ({ api, locals }) => {
    const res = await api("unpaid-invoices", {
      query: {
        user: locals.activeUserToRequest._id,
      },
    });
    const resJSON = await res.json();
    return resJSON.invoices;
  }
);
